import Truck from "./Truck";
import {
  OrbitControls,
  ScrollControls,
  useScroll,
  Html,
} from "@react-three/drei";
import * as THREE from "three";
import { useState, useRef, useMemo } from "react";
import Effects from "./Effects";
import Lights from "./Lights";
import Floor from "./Floor";
import Backdrop from "./Backdrop";
import { useFrame } from "@react-three/fiber";
import Intro from "./pages/Intro";
import Exo from "./pages/Exo";
import Utility from "./pages/Utility";
import Perform from "./pages/Perform";
import Final from "./pages/Final";
import { Perf } from "r3f-perf";
import { useControls, Leva } from "leva";
import OrderButton from "./pages/OrderButton";

export default function Experience() {
  const debugMode = useMemo(() => {
    return window.location.href.includes("#debug");
  }, []);

  const { showPerf, showAxes, showOrbitControl } = useControls("debug", {
    showPerf: false,
    showAxes: false,
    showOrbitControl: false,
  });

  const { near, far } = useControls("fog", {
    near: 15,
    far: 30,
  });

  return (
    <>
      {debugMode ? null : <Leva hidden />}
      {showPerf ? <Perf position="top-left" /> : null}
      {showOrbitControl ? <OrbitControls makeDefault /> : null}
      {showAxes ? <axesHelper scale={5} /> : null}
      <color attach="background" args={["#101010"]} />
      <fog attach="fog" args={["#17171b", near, far]} />
      <Effects />
      <ScrollControls pages={7}>
        <Composition />
      </ScrollControls>
    </>
  );
}

function Composition() {
  const scroll = useScroll();
  const group = useRef();
  const backdrop = useRef();
  const [showVideo, setShowVideo] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visible1, setVisible1] = useState(false);
  const [visible2, setVisible2] = useState(false);
  const [visible3, setVisible3] = useState(false);
  const [visible4, setVisible4] = useState(false);

  useFrame((state, delta) => {
    const r1 = scroll.range(0 / 7, 1 / 7);
    const r3 = scroll.range(2 / 7, 1 / 7);
    const r4 = scroll.range(4 / 7, 1 / 7);
    const r5 = scroll.range(6 / 7, 1 / 7);

    const t1 = scroll.visible(0 / 7, 0.2 / 7);
    const t2 = scroll.visible(1 / 7, 1.5 / 7);
    const t3 = scroll.visible(3 / 7, 1 / 7);
    const t4 = scroll.visible(4.5 / 7, 1.8 / 7);
    const t5 = scroll.visible(6.5 / 7, 1 / 7);
    const t6 = scroll.visible(3 / 7, 4 / 7);

    setVisible(t1);
    setVisible1(t2);
    setVisible2(t3);
    setVisible3(t4);
    setVisible4(t5);

    // change video
    setShowVideo(t6 && t5 ? "pause" : t6 ? "play" : false);

    group.current.rotation.y = THREE.MathUtils.damp(
      group.current.rotation.y,
      (-Math.PI / 2) * r1 + (-Math.PI / 2) * r3 + -Math.PI * r5,
      4,
      delta
    );
    group.current.rotation.x = THREE.MathUtils.damp(
      group.current.rotation.x,
      (Math.PI / 2) * 0.1 * r3,
      4,
      delta
    );
    backdrop.current.position.z = THREE.MathUtils.damp(
      backdrop.current.position.z,
      19 * r4 + -8,
      4,
      delta
    );
    group.current.position.y = THREE.MathUtils.damp(
      group.current.position.y,
      -2 * r4 + 2 * r5,
      4,
      delta
    );
  });

  return (
    <group>
      <group ref={group}>
        <Lights />
        <Truck />
        <Floor />
        <Backdrop ref={backdrop} showVideo={showVideo} />
      </group>
      <Intro visible={visible} />
      <Exo visible={visible1} />
      <Utility visible={visible2} />
      <Perform visible={visible3} />
      <Final visible={visible4} />
      <OrderButton visible={visible || visible4} />
    </group>
  );
}
